/*
 *  -----------------------------------------------------------------------
 *    <copyright file="local-state-context.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useReducer, useMemo, Dispatch, PropsWithChildren } from 'react';
import { CM2Dataset } from '@interfaces/central-monitoring';
import { FieldDefinitionDto, RowDataSourceDto } from '@interfaces-clean/field-rdl-definition.interface';
import { ExtendedDatasetFieldsWithIcon } from '@utils/cm-observations.service';
import { Logger } from '@utils/logger';
import { getFieldsAndDataSources } from '@queries/generated/central-monitoring.generated';
import { OrganisationFields, StudyFields, NavbarStudyFields } from '@queries/generated/fragments.generated';
import { ProcessedStudyDataResponse, ScoringStudyDataResponse } from '@apis/bff-api/studies';

export enum LocalContextPersistedKey {
    StudyId = 'studyId',
    DatasetId = 'datasetId',
    LockedToOrgKey = 'locked-to-org-key',
    SiteCode = 'site-code',
    SubjectCode = 'subjectCode',
    Token = 'token',
    AllowEdit = 'allowEdit',
}

export const getSessionStorageToken = () => sessionStorage?.getItem(LocalContextPersistedKey.Token);

export const setSessionStorageSubjectCode = (subjectCode: string | null) => {
    // We don't need to return if it exists already, just set whatever we want to set.
    sessionStorage.setItem(LocalContextPersistedKey.SubjectCode, subjectCode ?? '');
};

export const setSessionStorageStudyId = (studyId: string | null) => {
    if (sessionStorage.getItem(LocalContextPersistedKey.LockedToOrgKey)) {
        return;
    }
    sessionStorage.setItem(LocalContextPersistedKey.StudyId, studyId ?? '');
};

export const setSessionStorageDatasetId = (datasetId: string | null | undefined) => {
    if (sessionStorage.getItem(LocalContextPersistedKey.LockedToOrgKey)) {
        return;
    }
    sessionStorage.setItem(LocalContextPersistedKey.DatasetId, datasetId ?? '');
};

export const setSessionStorageSiteCode = (pageData: string | null | undefined) => {
    sessionStorage.setItem(LocalContextPersistedKey.SiteCode, pageData ?? '');
};

export const getSessionStorageSubjectCode = () => sessionStorage?.getItem(LocalContextPersistedKey.SubjectCode);

export const getSessionStorageStudyId = () => sessionStorage?.getItem(LocalContextPersistedKey.StudyId);

export const getSessionStorageDatasetId = () => sessionStorage?.getItem(LocalContextPersistedKey.DatasetId);

export const getSessionStorageSiteCode = () => sessionStorage?.getItem(LocalContextPersistedKey.SiteCode);

interface ILocalStateContext {
    dispatch: Dispatch<ILocalStateAction>;
    state: ILocalState | null;
}

export type NullableOrganisation = OrganisationFields | null;

export type NullableStudy = StudyFields | null;

export type NullableLegacyDataset = ExtendedDatasetFieldsWithIcon | null;

export type DataViewConfigItem = { id: string; name: string };

export interface ILocalState {
    // These are legacy CM RDLs only - don't use for anything that isn't legacy CM
    dataSources?: getFieldsAndDataSources['rowDataSources'] | null;
    datasetDate?: NullableLegacyDataset;
    // These are legacy CM calculated fields only - don't use for anything that isn't legacy CM
    fields?: getFieldsAndDataSources['fields'] | null;
    organisation?: NullableOrganisation;
    studies?: NavbarStudyFields[];
    study?: NullableStudy;
    /** Scoring study datasets from /v1/studies/id/${study.id}/kri/${datasetId} */
    scoringData?: ScoringStudyDataResponse | null;
    selectedDatasetDate?: NullableLegacyDataset;
    selectedCM2Dataset?: CM2Dataset | null;
    selectedComparisonCM2Dataset?: CM2Dataset | null;
    selectedOrganisation?: NullableOrganisation;
    selectedStudy?: NullableStudy;
    /** Processed study datasets from /v1/studies/id/${study.id}/data/${datasetId} */
    studyData?: ProcessedStudyDataResponse | null;
    userStudies?: NavbarStudyFields[] | null;
    timestamp?: Date;
    dataViewConfig?: DataViewConfigItem[] | null;

    selectedSubjectCode?: string | null;
    setSubjectProfileNavigation?: boolean | null;

    // Use these for CM2 (none legacy CM) calculated field definitions and RDLs
    cm2Fields?: FieldDefinitionDto[] | null;
    cm2DataSources?: RowDataSourceDto[] | null;
}

export type ILocalStateAction = ILocalState & {
    type: ILocalStateActionType;
    value?: Date;
    data?: ProcessedStudyDataResponse | ScoringStudyDataResponse | null;
    config?: DataViewConfigItem[];
};

type ILocalStateActionType =
    | 'CLEAR_TIMESTAMP'
    | 'SELECT_ORGANISATION'
    | 'SELECT_STUDY'
    | 'SET_DATA_SOURCES'
    | 'SET_FIELDS'
    | 'SET_PROCESSED_DATA'
    | 'SET_SCORING_DATA'
    | 'SET_SELECTED_DATE'
    | 'SET_SELECTED_CM2_DATASET'
    | 'SET_SELECTED_COMPARISON_CM2_DATASET'
    | 'SET_TIMESTAMP'
    | 'SET_USER_STUDIES'
    | 'SET_DATA_VIEW_CONFIG'
    | 'SET_SUBJECT_PROFILE_NAVIGATION'
    | 'SET_CM2_FIELDS_RDLS';

const defaultSettings: ILocalState = {
    dataSources: null,
    fields: null,
    scoringData: null,
    selectedDatasetDate: null,
    selectedCM2Dataset: null,
    selectedComparisonCM2Dataset: null,
    selectedOrganisation: null,
    selectedStudy: null,
    studyData: null,
    userStudies: null,
    dataViewConfig: null,
    selectedSubjectCode: null,
    setSubjectProfileNavigation: null,
    cm2Fields: null,
    cm2DataSources: null,
};

export const LocalStateContext = React.createContext<ILocalStateContext>({
    state: null,
    dispatch: null as unknown as ILocalStateContext['dispatch'],
});

const isLoggingEnabled = import.meta.env.VITE_QUIETEN_LOGS !== 'true';

const reducer = (state: ILocalState, action: ILocalStateAction): ILocalState => {
    switch (action.type) {
        case 'SET_TIMESTAMP':
            return {
                ...state,
                timestamp: action.value as Date,
            };

        case 'SET_PROCESSED_DATA':
            isLoggingEnabled && Logger.log('Setting Processed Data: ', action.data);
            return {
                ...state,
                studyData: action.data as ProcessedStudyDataResponse,
            };

        case 'SET_SCORING_DATA':
            isLoggingEnabled && Logger.log('Setting Scoring Data: ', action.data);
            return {
                ...state,
                scoringData: action.data as ScoringStudyDataResponse,
            };

        case 'CLEAR_TIMESTAMP':
            return {
                ...state,
                timestamp: new Date(),
            };

        case 'SELECT_ORGANISATION':
            return {
                ...state,
                selectedOrganisation: action.organisation,
            };

        case 'SELECT_STUDY':
            return {
                ...state,
                selectedStudy: action.study,
            };

        case 'SET_FIELDS':
            isLoggingEnabled && Logger.log('Setting Legacy Field Data: ', action.fields);
            return {
                ...state,
                fields: action.fields,
            };

        case 'SET_DATA_SOURCES':
            isLoggingEnabled && Logger.log('Setting Legacy Data Sources: ', action.dataSources);
            return {
                ...state,
                dataSources: action.dataSources,
            };

        case 'SET_CM2_FIELDS_RDLS':
            isLoggingEnabled &&
                Logger.log('Setting CM2 Fields & Data Sources: ', action.cm2Fields, action.cm2DataSources);
            return {
                ...state,
                cm2Fields: action.cm2Fields,
                cm2DataSources: action.cm2DataSources,
            };

        case 'SET_USER_STUDIES':
            isLoggingEnabled && Logger.log('Setting User Studies: ', action.studies);
            return {
                ...state,
                userStudies: action.studies,
            };

        case 'SET_SELECTED_DATE':
            isLoggingEnabled && Logger.log('Setting Legacy Dataset Date: ', action.datasetDate);
            return {
                ...state,
                selectedDatasetDate: action.datasetDate,
            };

        case 'SET_SELECTED_CM2_DATASET':
            isLoggingEnabled && Logger.log('Setting CM2 Dataset: ', action.selectedCM2Dataset);
            return {
                ...state,
                selectedCM2Dataset: action.selectedCM2Dataset,
            };

        case 'SET_SELECTED_COMPARISON_CM2_DATASET':
            isLoggingEnabled && Logger.log('Setting Comparison CM2 Dataset: ', action.selectedComparisonCM2Dataset);
            return {
                ...state,
                selectedComparisonCM2Dataset: action.selectedComparisonCM2Dataset,
            };

        case 'SET_DATA_VIEW_CONFIG':
            return {
                ...state,
                dataViewConfig: action.config,
            };

        case 'SET_SUBJECT_PROFILE_NAVIGATION':
            return {
                ...state,
                setSubjectProfileNavigation: action.setSubjectProfileNavigation,
            };
        default:
            throw new Error();
    }
};

const LocalStateContextProvider = ({ children }: PropsWithChildren) => {
    const [state, dispatch] = useReducer(reducer, defaultSettings);

    return (
        <LocalStateContext.Provider value={useMemo(() => ({ state, dispatch }), [state, dispatch])}>
            {children}
        </LocalStateContext.Provider>
    );
};

export default LocalStateContextProvider;
